import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Coins, CalendarBlank, Lightning, ListChecks, Trophy, CaretDoubleRight } from "@phosphor-icons/react";
import { fetchContent, trackEvent, FALLBACK_CONTENT_V1_HUD } from "@/lib/content";

const EASE = [0.22, 1, 0.36, 1];
const PINK = "#FF4D8D";
const GOLD = "#F5B60D";
const DARK = "#1A0B2E";
const REDUCED = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Renders **bold** segments so any admin-edited text can highlight words.
const renderRich = (text) =>
  String(text ?? "")
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part, i) =>
      part.startsWith("**") && part.endsWith("**") ? (
        <strong key={i} className="font-bold">{part.slice(2, -2)}</strong>
      ) : (
        part
      )
    );

function Countdown({ startDate, startsIn = "Starts in", liveWord = "Live", dayLabel = "Day" }) {
  const calc = () => {
    const diff = new Date(startDate).getTime() - Date.now();
    if (isNaN(diff)) return null;
    if (diff <= 0) return "live";
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff % 86400000) / 3600000),
      m: Math.floor((diff % 3600000) / 60000),
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    setT(calc());
    const id = setInterval(() => setT(calc()), 30000);
    return () => clearInterval(id);
  }, [startDate]);
  if (!t) return null;
  const pad = (n) => String(n).padStart(2, "0");
  if (t === "live") {
    const day = Math.max(1, Math.floor((Date.now() - new Date(startDate).getTime()) / 86400000) + 1);
    return (
      <span className="inline-flex items-center gap-2.5 font-inter-body text-[11px] font-bold tracking-[0.3em] uppercase text-[#E3D2DC]/80" data-testid="hero-countdown">
        <span className="w-2 h-2 rounded-full stage-dot" style={{ background: PINK, boxShadow: `0 0 10px ${PINK}` }} />
        {liveWord}
        <span className="cz-glass rounded-lg px-3 py-1.5 font-chakra text-lg md:text-xl tracking-wide text-white" style={{ boxShadow: "0 0 18px rgba(255,77,141,0.18)" }} data-testid="hero-day">
          {dayLabel} {day}
        </span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-3 font-inter-body text-[11px] font-bold tracking-[0.3em] uppercase text-[#E3D2DC]/80" data-testid="hero-countdown">
      {startsIn}
      <span className="flex items-baseline gap-1.5">
        {[["D", t.d], ["H", t.h], ["M", t.m]].map(([u, v]) => (
          <span key={u} className="cz-glass rounded-lg px-2.5 py-1.5 font-chakra text-lg md:text-xl tracking-wide text-white" style={{ boxShadow: "0 0 18px rgba(255,77,141,0.18)" }}>
            {pad(v)}<span className="text-[11px] ml-0.5" style={{ color: PINK }}>{u}</span>
          </span>
        ))}
      </span>
    </span>
  );
}

function Chip({ icon: Icon, children, testid }) {
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-inter-body text-[11px] md:text-xs font-semibold tracking-[0.1em] uppercase text-[#F1E4EC] cz-glass"
      style={{ border: "1px solid rgba(255,77,141,0.28)" }}
      data-testid={testid}
    >
      <Icon size={15} weight="fill" style={{ color: GOLD }} />
      {children}
    </span>
  );
}

const SPARKS = [
  { top: "10%", left: "20%", s: 3, c: GOLD, d: "0s", dur: "4.2s" },
  { top: "8%", left: "52%", s: 2, c: "#FFFFFF", d: "1.1s", dur: "3.6s" },
  { top: "18%", left: "70%", s: 3, c: PINK, d: "0.5s", dur: "5s" },
  { top: "24%", left: "38%", s: 2, c: GOLD, d: "1.8s", dur: "4.6s" },
  { top: "30%", left: "60%", s: 4, c: GOLD, d: "0.3s", dur: "5.4s" },
  { top: "44%", left: "48%", s: 2, c: "#FFFFFF", d: "2.2s", dur: "4s" },
  { top: "40%", left: "16%", s: 3, c: PINK, d: "1.4s", dur: "4.8s" },
  { top: "58%", left: "34%", s: 2, c: GOLD, d: "0.9s", dur: "3.8s" },
  { top: "66%", left: "58%", s: 3, c: "#FFFFFF", d: "2.6s", dur: "5.2s" },
  { top: "72%", left: "24%", s: 2, c: GOLD, d: "0.2s", dur: "4.4s" },
  { top: "52%", left: "66%", s: 2, c: PINK, d: "1.6s", dur: "4.2s" },
  { top: "84%", left: "44%", s: 3, c: GOLD, d: "0.7s", dur: "5s" },
  { top: "14%", left: "84%", s: 2, c: GOLD, d: "2.0s", dur: "4.6s" },
  { top: "78%", left: "62%", s: 2, c: "#FFFFFF", d: "1.2s", dur: "3.9s" },
];

function Sparkles() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {SPARKS.map((sp, i) => (
        <span
          key={i}
          className="cz-spark"
          style={{
            top: sp.top,
            left: sp.left,
            width: sp.s,
            height: sp.s,
            background: sp.c,
            boxShadow: `0 0 ${sp.s * 3}px ${sp.c}`,
            "--delay": sp.d,
            "--dur": sp.dur,
          }}
        />
      ))}
    </div>
  );
}

const COINS = [
  { l: "6%", s: 22, d: "0s", dur: "1.1s" },
  { l: "14%", s: 15, d: "0.2s", dur: "1.3s" },
  { l: "22%", s: 26, d: "0.05s", dur: "0.95s" },
  { l: "30%", s: 18, d: "0.3s", dur: "1.15s" },
  { l: "38%", s: 13, d: "0.1s", dur: "1.35s" },
  { l: "46%", s: 24, d: "0.25s", dur: "1.0s" },
  { l: "54%", s: 16, d: "0s", dur: "1.2s" },
  { l: "62%", s: 20, d: "0.35s", dur: "1.1s" },
  { l: "70%", s: 14, d: "0.15s", dur: "1.3s" },
  { l: "78%", s: 27, d: "0.08s", dur: "0.95s" },
  { l: "86%", s: 17, d: "0.28s", dur: "1.2s" },
  { l: "94%", s: 21, d: "0.2s", dur: "1.05s" },
  { l: "10%", s: 16, d: "0.42s", dur: "1.15s" },
  { l: "26%", s: 19, d: "0.48s", dur: "1.1s" },
  { l: "42%", s: 14, d: "0.38s", dur: "1.35s" },
  { l: "58%", s: 23, d: "0.5s", dur: "0.95s" },
  { l: "74%", s: 15, d: "0.44s", dur: "1.25s" },
  { l: "90%", s: 20, d: "0.55s", dur: "1.1s" },
  { l: "18%", s: 13, d: "0.6s", dur: "1.3s" },
  { l: "50%", s: 25, d: "0.62s", dur: "0.95s" },
  { l: "66%", s: 17, d: "0.58s", dur: "1.15s" },
  { l: "82%", s: 18, d: "0.65s", dur: "1.1s" },
];

function CoinShower() {
  const [show, setShow] = useState(!REDUCED);
  useEffect(() => {
    if (REDUCED) return;
    const t = setTimeout(() => setShow(false), 2000);
    return () => clearTimeout(t);
  }, []);
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-50 overflow-hidden pointer-events-none" aria-hidden="true" data-testid="coin-shower">
      {COINS.map((c, i) => (
        <span key={i} className="cz-coin" style={{ left: c.l, width: c.s, height: c.s, animationDelay: c.d, animationDuration: c.dur }} />
      ))}
    </div>
  );
}



function FlipCard({ index, name, Icon, flipped, onToggle, testid, children, tapLabel = "Tap to flip", backLabel = "Flip back" }) {
  return (
    <div className={`cz-flip h-[340px] md:h-[380px] ${flipped ? "is-flipped" : ""}`} data-testid={testid}>
      <button
        type="button"
        onClick={onToggle}
        className="cz-flip-inner appearance-none bg-transparent border-0 p-0 text-left"
        aria-label={`Flip ${name} card`}
        data-testid={`${testid}-toggle`}
      >
        {/* FRONT */}
        <div className="cz-flip-face flex flex-col items-center justify-center text-center p-7 md:p-8">
          <span className="font-chakra absolute top-3 right-4 text-5xl leading-none select-none tracking-[-0.02em]" style={{ color: "rgba(26,11,46,0.16)" }}>{index}</span>
          <span className="inline-flex items-center justify-center w-16 h-16 rounded-full" style={{ background: DARK, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.1), 0 6px 16px rgba(0,0,0,0.3)" }}>
            <Icon size={28} weight="fill" style={{ color: GOLD }} />
          </span>
          <h3 className="mt-5 font-chakra uppercase text-2xl md:text-[1.75rem] font-bold leading-[1.04] tracking-[0.02em]" style={{ color: DARK }}>{name}</h3>
          <span className="mt-4 inline-flex items-center gap-1.5 font-inter-body text-[10px] font-extrabold tracking-[0.24em] uppercase" style={{ color: "rgba(26,11,46,0.6)" }}>
            {tapLabel} <ArrowRight size={12} weight="bold" />
          </span>
        </div>
        {/* BACK: title (top) -> content (middle) -> flip back (bottom) */}
        <div className="cz-flip-face cz-flip-back flex flex-col text-left p-7 md:p-8 overflow-hidden" style={{ color: DARK }}>
          <div>
            <span className="inline-flex items-center gap-1.5 font-chakra text-[11px] font-bold tracking-[0.22em] uppercase" style={{ color: "rgba(26,11,46,0.55)" }}>
              <Icon size={13} weight="fill" style={{ color: "rgba(26,11,46,0.55)" }} /> {index}
            </span>
            <h4 className="mt-1.5 font-chakra uppercase font-bold text-[1.55rem] md:text-[1.8rem] leading-[1.02] tracking-[0.01em]" style={{ color: DARK }}>{name}</h4>
          </div>
          <div className="flex-1 flex flex-col min-h-0 py-3">{children}</div>
          <span className="inline-flex items-center gap-1.5 font-inter-body text-[9px] font-extrabold tracking-[0.24em] uppercase" style={{ color: "rgba(26,11,46,0.5)" }}>
            <ArrowLeft size={11} weight="bold" /> {backLabel}
          </span>
        </div>
      </button>
    </div>
  );
}

export default function CasinoPage({ variant = "v1" }) {
  const [content, setContent] = useState(FALLBACK_CONTENT_V1_HUD);
  const [screen, setScreen] = useState(1);
  const [flipped, setFlipped] = useState([false, false, false]);
  const toggleFlip = (i) => setFlipped((f) => f.map((v, idx) => (idx === i ? !v : v)));

  useEffect(() => {
    fetchContent(variant)
      .then((data) => setContent({ ...FALLBACK_CONTENT_V1_HUD, ...data }))
      .catch(() => setContent(FALLBACK_CONTENT_V1_HUD));
  }, [variant]);

  const h = content.hero || {};
  const signin = content.signin || {};
  const ranks = content.ranks || {};
  const rankItem = (ranks.items || [])[0];
  const ladders = content.ladders || {};
  const tiers = ladders.single?.tiers || [];
  const guidelines = content.guidelines || {};
  const ui = content.ui || {};
  const ctaUrl = content.ctaUrl || "#";
  const ctaLabel = content.ctaText || "Log in to Leaderboard";
  const learnLabel = h.learnLabel || "Learn How to Earn";
  const dates = (h.dates || "Live Aug 1 – Aug 10").replace(/^Live\s*/i, "");
  const tierSuffix = ladders.tierSuffix || "conversions";
  const rankSuffix = ranks.rankSuffix || "Rank";
  const heading = (content.howToEarnHeading || "How to earn").trim();
  const headingWords = heading.split(" ");
  const headingLast = headingWords.length > 1 ? headingWords.pop() : heading;
  const headingPre = headingWords.length ? headingWords.join(" ") : "";

  const goEarn = () => {
    trackEvent("cta_click", "learn_how_to_earn", { screen: "landing" });
    setScreen(2);
  };

  const LoginCta = ({ loc }) => (
    <a
      href={ctaUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackEvent("cta_click", loc, { label: ctaLabel })}
      className="cz-btn-dark group inline-flex items-center justify-center gap-2.5 font-bebas text-xl md:text-2xl tracking-[0.08em] uppercase px-9 md:px-11 py-3.5"
      data-testid={`cta-login-${loc}`}
    >
      {ctaLabel}
      <CaretDoubleRight size={18} weight="bold" className="group-hover:translate-x-1.5 transition-transform duration-300" />
    </a>
  );

  return (
    <div
      className="relative w-full cz-bg text-white font-outfit overflow-x-clip flex flex-col md:h-[100dvh] md:overflow-hidden min-h-[100dvh]"
      data-testid="landing-page"
    >
      <div className="cz-noise" />
      {content.coinShower !== false && <CoinShower />}

      {/* ---------- NAV ---------- */}
      <header className="relative z-40 shrink-0 cz-glass border-x-0 border-t-0">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-14 md:h-16 flex items-center justify-between">
          <span className="font-bebas text-2xl tracking-[0.1em]" data-testid="brand-label">
            {h.brandLead || "Emergent's"} <span style={{ color: PINK }}>{h.brandAccent || "Game Room"}</span>
          </span>
          <span className="hidden sm:inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-1.5 font-outfit text-[11px] font-bold tracking-[0.2em] uppercase text-[#E3D2DC]" data-testid="hero-dates">
            <span className="w-1.5 h-1.5 rounded-full stage-dot" style={{ background: GOLD, boxShadow: `0 0 8px ${GOLD}` }} />
            {h.dates || "Live Aug 1 – Aug 10"}
          </span>
        </div>
      </header>

      {/* ---------- STAGE ---------- */}
      <main className="relative z-10 flex-1 flex flex-col overflow-hidden">
        <AnimatePresence mode="wait" initial={false}>
          {screen === 1 ? (
            /* ============ SCREEN 1 - LANDING ============ */
            <motion.section
              key="screen-1"
              initial={REDUCED ? false : { opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={REDUCED ? { opacity: 0 } : { opacity: 0, x: -40 }}
              transition={{ duration: 0.55, ease: EASE }}
              className="relative flex-1 flex items-center"
              data-testid="screen-landing"
            >
              {/* subtle twinkling sparkles to fill space */}
              <Sparkles />
              {/* warm spotlight glow behind wheel */}
              <span
                className="absolute right-[-6%] md:right-[4%] top-1/2 -translate-y-1/2 w-[92vw] md:w-[74vh] md:max-w-[42rem] md:h-[74vh] md:max-h-[42rem] aspect-square md:aspect-auto rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(245,182,13,0.28) 0%, rgba(255,77,141,0.14) 34%, rgba(69,10,171,0.34) 55%, transparent 72%)", filter: "blur(18px)" }}
                aria-hidden="true"
              />
              {/* single centerpiece: roulette wheel — centering on plain wrapper, scale on motion, float on img (avoids transform conflicts) */}
              <div
                className="absolute right-[-10%] md:right-[5%] top-1/2 -translate-y-1/2 w-[82vw] md:w-[68vh] md:max-w-[37rem] md:h-[68vh] md:max-h-[37rem] aspect-square md:aspect-auto pointer-events-none select-none opacity-30 md:opacity-100"
                aria-hidden="true"
              >
                <motion.div
                  initial={REDUCED ? false : { opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1.1, delay: 0.2, ease: EASE }}
                  className="relative w-full h-full"
                >
                  <img
                    src="/assets/casino-roulette-clean.jpg"
                    alt=""
                    className="cz-blend cz-float-slow w-full h-full object-contain"
                    style={{
                      WebkitMaskImage: "radial-gradient(circle at 50% 50%, black 52%, transparent 74%)",
                      maskImage: "radial-gradient(circle at 50% 50%, black 52%, transparent 74%)",
                    }}
                  />
                  {/* rotating glint sweep over the wheel */}
                  <span
                    className="cz-glint absolute left-[18%] top-[18%] w-[64%] h-[64%] pointer-events-none"
                    aria-hidden="true"
                  />
                </motion.div>
              </div>

              <div className="relative w-full max-w-7xl mx-auto px-5 md:px-10">
                <div className="max-w-xl flex flex-col gap-8 md:gap-10">
                  {/* eyebrow + countdown */}
                  <motion.div
                    initial={REDUCED ? false : { opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: EASE }}
                    className="flex flex-wrap items-center gap-x-5 gap-y-3"
                  >
                    <span className="md:-ml-[17px] inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-inter-body text-[10px] md:text-[11px] font-bold tracking-[0.28em] uppercase" style={{ border: `1px solid ${PINK}66`, color: PINK, boxShadow: "0 0 20px rgba(255,77,141,0.15)" }} data-testid="hero-eyebrow">
                      {h.eyebrow || "Exclusive Invite · Emergent Game Room"}
                    </span>
                    {h.startDate && <Countdown startDate={h.startDate} startsIn={h.startsInLabel} liveWord={h.liveWord} dayLabel={h.dayLabel} />}
                  </motion.div>

                  {/* headline */}
                  <motion.h1
                    initial={REDUCED ? false : { opacity: 0, y: 28 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                    className="font-anton uppercase leading-[0.9]"
                    data-testid="hero-headline"
                  >
                    <span className="block text-white text-2xl sm:text-3xl lg:text-[2.6rem] tracking-[0.02em]" style={{ textShadow: "0 0 34px rgba(69,10,171,0.8)" }}>
                      {h.winPre || "Win up to"}
                    </span>
                    <span className="block cz-gold-pulse text-[3.5rem] sm:text-[4.75rem] lg:text-[6rem] tracking-[-0.01em] mt-1" style={{ color: GOLD }} data-testid="hero-max-total">
                      {h.maxTotal || "$2,000"}
                    </span>
                  </motion.h1>

                  {/* at-a-glance chips */}
                  <motion.div
                    initial={REDUCED ? false : { opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2, ease: EASE }}
                    className="md:-ml-[17px] flex flex-wrap gap-2.5"
                    data-testid="hero-chips"
                  >
                    <Chip icon={Coins} testid="chip-signin">{h.signinChip || `$${signin.amount || 30} just for signing in`}</Chip>
                    <Chip icon={CalendarBlank} testid="chip-dates">{dates}</Chip>
                  </motion.div>

                  {/* CTAs */}
                  <motion.div
                    initial={REDUCED ? false : { opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.32, ease: EASE }}
                    className="md:-ml-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5"
                  >
                    <button
                      type="button"
                      onClick={goEarn}
                      className="cz-btn group inline-flex items-center justify-center gap-2.5 h-14 px-6 font-inter-body text-[13px] md:text-sm font-extrabold tracking-[0.16em] uppercase whitespace-nowrap"
                      data-testid="cta-learn-how-to-earn"
                    >
                      {learnLabel}
                      <ArrowRight size={17} weight="bold" className="group-hover:translate-x-1.5 transition-transform duration-300" />
                    </button>
                    <a
                      href={ctaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent("cta_click", "landing_login", { label: ctaLabel })}
                      className="inline-flex items-center justify-center gap-2 h-14 px-6 rounded-full font-inter-body text-[13px] md:text-sm font-extrabold tracking-[0.16em] uppercase whitespace-nowrap text-white transition-all duration-300 hover:bg-white/5"
                      style={{ border: "1px solid rgba(255,255,255,0.35)" }}
                      data-testid="cta-login-landing"
                    >
                      {ctaLabel}
                    </a>
                  </motion.div>
                </div>
              </div>
            </motion.section>
          ) : (
            /* ============ SCREEN 2 - HOW TO EARN ============ */
            <motion.section
              key="screen-2"
              initial={REDUCED ? false : { opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={REDUCED ? { opacity: 0 } : { opacity: 0, x: 40 }}
              transition={{ duration: 0.55, ease: EASE }}
              className="relative flex-1 flex flex-col md:overflow-hidden overflow-y-auto"
              data-testid="screen-how-to-earn"
            >
              {/* subtle twinkling sparkles */}
              <Sparkles />
              <span
                className="absolute left-1/2 top-0 -translate-x-1/2 w-[70rem] h-[24rem] max-w-[95vw] rounded-full pointer-events-none"
                style={{ background: "radial-gradient(ellipse, rgba(245,182,13,0.1) 0%, rgba(255,77,141,0.06) 45%, transparent 70%)" }}
                aria-hidden="true"
              />
              <div className="relative max-w-7xl mx-auto w-full px-5 md:px-10 py-6 md:py-8 flex-1 flex flex-col">
                {/* header row */}
                <div className="flex items-center justify-between gap-4">
                  <button
                    type="button"
                    onClick={() => { setFlipped([false, false, false]); setScreen(1); }}
                    className="inline-flex items-center gap-1.5 font-outfit text-[11px] md:text-xs font-bold tracking-[0.24em] uppercase text-[#E3D2DC]/80 hover:text-white transition-colors"
                    data-testid="back-to-landing"
                  >
                    <ArrowLeft size={16} weight="bold" /> {ui.back || "Back"}
                  </button>
                  <span className="font-bebas text-2xl md:text-3xl uppercase tracking-[0.02em] text-white text-right leading-none" data-testid="how-to-earn-heading">
                    {headingPre} <span style={{ color: GOLD }}>{headingLast}</span>
                  </span>
                </div>

                {/* three flip cards */}
                <div className="mt-5 md:mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 flex-1">
                  {/* CARD 1 · SIGN-IN BONUS */}
                  <FlipCard index="01" name={signin.title || "Sign-in Bonus"} Icon={Coins} flipped={flipped[0]} onToggle={() => toggleFlip(0)} testid="flip-card-signin" tapLabel={ui.tapToFlip} backLabel={ui.flipBack}>
                    <div className="flex flex-col items-center justify-center text-center h-full" style={{ color: DARK }}>
                      <span className="font-chakra leading-none text-[4rem] md:text-[4.5rem] tracking-[-0.02em]" data-testid="signin-amount">
                        ${signin.amount || 30}
                      </span>
                      <p className="mt-4 font-inter-body text-[15px] leading-relaxed" data-testid="signin-body">
                        {signin.body || `You can earn $${signin.amount || 30} just by signing up.`}
                      </p>
                      <p className="mt-2 font-inter-body text-[13px] leading-relaxed" style={{ color: "rgba(26,11,46,0.68)" }} data-testid="signin-note">
                        {signin.note || "Only if you log in during the campaign window."}
                      </p>
                    </div>
                  </FlipCard>

                  {/* CARD 2 · CONVERSION MILESTONES */}
                  <FlipCard index="02" name={ranks.cardTitle || "Ranking and Milestones"} Icon={Lightning} flipped={flipped[1]} onToggle={() => toggleFlip(1)} testid="flip-card-conversions" tapLabel={ui.tapToFlip} backLabel={ui.flipBack}>
                    <div className="flex flex-col justify-between h-full" style={{ color: DARK }}>
                      {rankItem && (
                        <div className="flex-1 flex items-center justify-between gap-3" data-testid="rank-row">
                          <span className="flex items-center gap-1.5 font-inter-body text-sm md:text-[15px] font-bold">
                            <Trophy size={15} weight="fill" /> {rankItem.rank} {rankSuffix}
                          </span>
                          <span className="font-chakra leading-none tracking-[-0.02em] text-2xl md:text-[1.85rem]" style={{ fontWeight: 700 }} data-testid="rank-amount">
                            ${rankItem.amount.toLocaleString()}
                          </span>
                        </div>
                      )}
                      {tiers.map((t, i) => {
                        const top = i === tiers.length - 1;
                        const sizes = ["text-lg md:text-xl", "text-xl md:text-2xl", "text-2xl md:text-[1.85rem]"];
                        const sizeCls = sizes[i] || sizes[sizes.length - 1];
                        return (
                          <div key={i} className="flex-1 flex items-center justify-between gap-3" style={{ borderTop: "1px solid rgba(26,11,46,0.18)" }} data-testid={`tier-row-${i + 1}`}>
                            <span className="font-inter-body text-sm md:text-[15px] font-semibold" style={{ opacity: top ? 1 : 0.9 }}>
                              {t.count} {tierSuffix}
                            </span>
                            <span className={`font-chakra leading-none tracking-[-0.02em] ${sizeCls}`} style={{ fontWeight: top ? 700 : 600 }} data-testid={`tier-amount-${i + 1}`}>
                              ${t.amount.toLocaleString()}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </FlipCard>

                  {/* CARD 3 · GUIDELINES */}
                  <FlipCard index="03" name={guidelines.title || "Guidelines"} Icon={ListChecks} flipped={flipped[2]} onToggle={() => toggleFlip(2)} testid="flip-card-guidelines" tapLabel={ui.tapToFlip} backLabel={ui.flipBack}>
                    <ul className="flex flex-col justify-center h-full gap-5 font-inter-body text-sm md:text-[14px] leading-relaxed" style={{ color: DARK }}>
                      {(guidelines.items || []).map((g, i) => (
                        <li className="flex gap-3 items-start" data-testid={`rule-${i + 1}`} key={i}>
                          <span className="mt-[7px] shrink-0 w-2 h-2 rounded-full" style={{ background: DARK }} />
                          <span>{renderRich(g)}</span>
                        </li>
                      ))}
                    </ul>
                  </FlipCard>
                </div>

                {/* CTA */}
                <div className="mt-6 md:mt-7 flex items-center justify-center shrink-0">
                  <LoginCta loc="how-to-earn" />
                </div>
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
